import {
  Fragment,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { marked, Renderer } from "marked";
import type { SearchToolResult, WorldCard } from "../server/astrolens";

interface MessagePartLike {
  type: string;
  text?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

interface ChargeMetadata {
  points: number;
  basePoints: number;
  additionalPoints: number;
  extendedPoints: number;
  apiCostUsd: number;
  charged: boolean;
}

interface MessageMetadataLike {
  charge?: ChargeMetadata;
}

function ReasoningAccordion({
  text,
  state,
}: {
  text: string;
  state?: string;
}) {
  const isStreaming = state !== "done";
  const [isOpen, setIsOpen] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!isStreaming) {
      setElapsedMs(Date.now() - startedAt);
      setIsOpen(false);
      return;
    }

    const timerId = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 250);

    return () => window.clearInterval(timerId);
  }, [isStreaming, startedAt]);

  const elapsedSeconds = Math.max(1, Math.round(elapsedMs / 1000));
  const label = isStreaming
    ? "思考中"
    : `${elapsedSeconds.toLocaleString()}秒思考しました`;

  return (
    <details
      className={`reasoning ${isStreaming ? "reasoning--streaming" : "reasoning--done"}`}
      open={isOpen}
      onToggle={(event) => {
        setIsOpen((event.currentTarget as HTMLDetailsElement).open);
      }}
    >
      <summary className="reasoning__summary" title="思考過程の詳細を開閉">
        <span className="reasoning__summary-label">
          <span className="tool__summary-caret reasoning__summary-caret" aria-hidden="true">
            &gt;
          </span>
          <span
            className={
              isStreaming
                ? "status__thinking-text reasoning__status-text"
                : "reasoning__status-text reasoning__status-text--done"
            }
          >
            {label}
          </span>
        </span>
      </summary>

      <div className="reasoning__content">
        <div className="reasoning__content-inner">
          <div className="reasoning__card">
            <div className="reasoning__text">{text}</div>
          </div>
        </div>
      </div>
    </details>
  );
}

function formatPoints(value: number) {
  return Number(value.toFixed(4)).toString();
}

export interface RenderableMessage {
  id: string;
  role: "user" | "assistant" | "system";
  metadata?: MessageMetadataLike;
  parts?: MessagePartLike[];
}

export interface SessionWorldRegistry {
  [ref: number]: WorldCard;
}

marked.setOptions({
  breaks: true,
  gfm: true,
});

const renderer = new Renderer();
renderer.link = ({ href, title, text }) => {
  const isWorldRef = typeof href === "string" && /^ref:\/\/\d+$/.test(href);
  const safeTitle = title ? ` title="${title}"` : "";

  if (isWorldRef) {
    const ref = href.replace("ref://", "");
    const label = (text ?? "").trim();
    const button = `<a href="${href}" class="message__ref-link" data-world-ref="${ref}" aria-label="${label || `ref:${ref}`}"${safeTitle}><span class="material-symbols-outlined" aria-hidden="true">arrow_outward</span></a>`;

    if (label) {
      return `<span class="message__ref-inline"><span class="message__ref-text">${label}</span>${button}</span>`;
    }

    return button;
  }

  return `<a href="${href ?? "#"}"${safeTitle}>${text}</a>`;
};

export function getWorldRefNumber(ref: WorldCard["ref"]) {
  if (typeof ref === "number" && Number.isFinite(ref)) {
    return ref;
  }

  const match = String(ref).match(/^ref:\/\/(\d+)$/);
  if (!match) {
    return 0;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatWorldRefLabel(ref: WorldCard["ref"]) {
  const numericRef = getWorldRefNumber(ref);
  return numericRef > 0 ? String(numericRef) : String(ref);
}

export function extractWorldRefs(text: string) {
  const matches = text.matchAll(/ref:\/\/(\d+)/g);
  const refs: number[] = [];

  for (const match of matches) {
    const ref = Number(match[1]);
    if (Number.isFinite(ref) && !refs.includes(ref)) {
      refs.push(ref);
    }
  }

  return refs;
}

export function normalizeRefMarkdown(text: string) {
  return text
    .replace(/\[ref:\/\/(\d+)\]/g, "[](ref://$1)")
    .replace(/(?<!\()(?<!\[)ref:\/\/(\d+)(?!\))/g, "[](ref://$1)");
}

function WorldCarousel({
  worlds,
  ariaLabel,
}: {
  worlds: WorldCard[];
  ariaLabel: string;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  function scrollByDirection(direction: "prev" | "next") {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const amount = Math.max(container.clientWidth * 0.72, 260);
    container.scrollBy({
      left: direction === "next" ? amount : -amount,
      behavior: "smooth",
    });
  }

  return (
    <div className="world-carousel-wrap" role="region" aria-label={ariaLabel}>
      <button
        className="world-carousel__nav world-carousel__nav--prev"
        type="button"
        aria-label="前のワールド候補へ"
        title="前のワールド候補へ"
        onClick={() => scrollByDirection("prev")}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          chevron_left
        </span>
      </button>

      <div className="world-carousel" ref={scrollRef}>
        <div className="world-carousel__track">
          {worlds.map((world) => (
            <WorldCardPanel
              key={`${world.ref}-${world.id}`}
              title={`${world.name} の候補カード`}
              variant="compact"
              world={world}
            />
          ))}
        </div>
      </div>

      <button
        className="world-carousel__nav world-carousel__nav--next"
        type="button"
        aria-label="次のワールド候補へ"
        title="次のワールド候補へ"
        onClick={() => scrollByDirection("next")}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          chevron_right
        </span>
      </button>
    </div>
  );
}

function WorldCardPanel({
  world,
  title,
  variant,
}: {
  world: WorldCard;
  title?: string;
  variant: "compact" | "expanded";
}) {
  const imageUrl = world.image_url
    ? `https://assets.astrolens.dev/${world.image_url}`
    : undefined;
  const summaryHtml = useMemo(() => {
    if (variant !== "expanded" || !world.summary) {
      return "";
    }

    const rawHtml = marked.parse(world.summary, { renderer }) as string;
    return DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true },
      ALLOWED_URI_REGEXP:
        /^(?:(?:https?|mailto|tel|ref):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    });
  }, [variant, world.summary]);

  return (
    <article
      className={`world-card world-card--panel world-card--${variant}`}
      title={title}
    >
      {variant === "compact" ? (
        <>
          <div className="world-card__media">
            {imageUrl ? (
              <img
                alt={world.name}
                className="world-card__image"
                loading="lazy"
                src={imageUrl}
              />
            ) : (
              <div className="world-card__fallback">No Image</div>
            )}
          </div>

          <div className="world-card__title-row">
            <h4>{world.name}</h4>
          </div>

          <div className="world-card__platform-row">
            <span className="world-card__platform">
              {world.platform || "Windows"}
            </span>
          </div>

          <div className="world-card__stats" aria-label="World stats">
            <div className="world-card__stat-item">
              <span
                className="material-symbols-outlined world-card__stat-icon"
                aria-hidden="true"
              >
                visibility
              </span>
              <span className="world-card__stat-value">
                {world.visits.toLocaleString()}
              </span>
            </div>
            <div className="world-card__stat-item">
              <span
                className="material-symbols-outlined world-card__stat-icon"
                aria-hidden="true"
              >
                favorite
              </span>
              <span className="world-card__stat-value">
                {world.favorites.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="world-card__action">
            {world.source_url ? (
              <a
                className="world-card__link"
                href={world.source_url}
                rel="noreferrer"
                target="_blank"
                title="PlanetVRChatのページを開く"
              >
                PlanetVRChatで開く
              </a>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="world-card__media">
            {imageUrl ? (
              <img
                alt={world.name}
                className="world-card__image"
                loading="lazy"
                src={imageUrl}
              />
            ) : (
              <div className="world-card__fallback">No Image</div>
            )}
          </div>

          <div className="world-card__body">
            <div className="world-card__title-row">
              <h4>{world.name}</h4>
              <span className="world-card__platform">
                {world.platform || "Windows"}
              </span>
            </div>

            <div className="world-card__stats" aria-label="World stats">
              <div className="world-card__stat-item">
                <span
                  className="material-symbols-outlined world-card__stat-icon"
                  aria-hidden="true"
                >
                  visibility
                </span>
                <span className="world-card__stat-value">
                  {world.visits.toLocaleString()}
                </span>
              </div>
              <div className="world-card__stat-item">
                <span
                  className="material-symbols-outlined world-card__stat-icon"
                  aria-hidden="true"
                >
                  favorite
                </span>
                <span className="world-card__stat-value">
                  {world.favorites.toLocaleString()}
                </span>
              </div>
            </div>

            {world.summary ? (
              <div
                className="world-card__summary"
                dangerouslySetInnerHTML={{ __html: summaryHtml }}
              />
            ) : null}

            <div className="world-card__action">
              {world.source_url ? (
                <a
                  className="world-card__link"
                  href={world.source_url}
                  rel="noreferrer"
                  target="_blank"
                  title="PlanetVRChatのページを開く"
                >
                  PlanetVRChatで開く
                </a>
              ) : null}
            </div>
          </div>
        </>
      )}
    </article>
  );
}

function MarkdownText({
  text,
  registry,
}: {
  text: string;
  registry: SessionWorldRegistry;
}) {
  const normalizedText = useMemo(() => normalizeRefMarkdown(text), [text]);
  const [selectedWorldRef, setSelectedWorldRef] = useState<number | null>(null);
  const referencedWorlds = useMemo(
    () => extractWorldRefs(normalizedText),
    [normalizedText]
  );
  const selectedWorld =
    selectedWorldRef !== null ? registry[selectedWorldRef] : undefined;

  const sanitizedHtml = useMemo(() => {
    const rawHtml = marked.parse(normalizedText, { renderer }) as string;
    return DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true },
      ALLOWED_URI_REGEXP:
        /^(?:(?:https?|mailto|tel|ref):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    });
  }, [normalizedText]);

  function handleInternalWorldLinkClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const link = target.closest("a[data-world-ref]");
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }

    event.preventDefault();

    const ref = link.dataset.worldRef;
    if (!ref) {
      return;
    }

    const numericRef = Number(ref);
    if (!Number.isFinite(numericRef) || !registry[numericRef]) {
      return;
    }

    setSelectedWorldRef(numericRef);
  }

  useEffect(() => {
    if (selectedWorldRef === null) {
      return;
    }

    if (!referencedWorlds.includes(selectedWorldRef)) {
      setSelectedWorldRef(null);
    }
  }, [referencedWorlds, selectedWorldRef]);

  useEffect(() => {
    if (!selectedWorld) {
      return;
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSelectedWorldRef(null);
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedWorld]);

  useEffect(() => {
    if (!selectedWorld) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedWorld]);

  return (
    <>
      <div
        className="message__text message__text--markdown"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        onClick={handleInternalWorldLinkClick}
      />

      {selectedWorld && typeof document !== "undefined"
        ? createPortal(
            <div
              aria-modal="true"
              className="world-popup"
              role="dialog"
              onClick={() => setSelectedWorldRef(null)}
            >
              <div
                className="world-popup__panel"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  aria-label="ポップアップを閉じる"
                  className="world-popup__close"
                  onClick={() => setSelectedWorldRef(null)}
                  type="button"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    close
                  </span>
                </button>

                <WorldCardPanel
                  title={`${selectedWorld.name} の参照カード`}
                  variant="expanded"
                  world={selectedWorld}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function ToolResultCard({ result }: { result: SearchToolResult }) {
  return (
    <details className="tool tool--accordion">
      <summary className="tool__summary" title="ツール結果の詳細を開閉">
        <span className="tool__summary-label">
          <span className="tool__summary-caret" aria-hidden="true">
            &gt;
          </span>
          ツールを呼び出しました
        </span>
      </summary>

      <div className="tool__content">
        <div className="tool__content-inner">
          <div className="tool__card">
            <div className="tool__header">
              <div>
                <div className="tool__eyebrow">Tool Result</div>
                <h3>{result.title}</h3>
              </div>
              <p className="tool__meta">
                {result.worlds.length}件
                {result.worlds.length > 0
                  ? ` | ref:${formatWorldRefLabel(result.worlds[0].ref)}-${
                      formatWorldRefLabel(result.worlds[result.worlds.length - 1].ref)
                    }`
                  : ""}
              </p>
            </div>
            <WorldCarousel ariaLabel={result.title} worlds={result.worlds} />
          </div>
        </div>
      </div>
    </details>
  );
}

export function formatToolLabel(partType: string) {
  const toolName = partType.replace(/^tool-/, "");

  if (toolName === "search_worlds") {
    return "search_worlds";
  }

  if (toolName === "get_similar_worlds") {
    return "get_similar_worlds";
  }

  return toolName;
}

export function formatToolOptionValue(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "未指定";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => formatToolOptionValue(entry)).join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, entry]) => `${key}: ${formatToolOptionValue(entry)}`)
      .join(" / ");
  }

  return String(value);
}

export function buildToolDetails(
  part: MessagePartLike,
  result: SearchToolResult
): Array<{ label: string; value: string }> {
  const input =
    part.input && typeof part.input === "object"
      ? (part.input as Record<string, unknown>)
      : {};

  const details: Array<{ label: string; value: string }> = [
    { label: "Tool", value: formatToolLabel(part.type) },
  ];

  if (part.type === "tool-search_worlds") {
    details.push({
      label: "Query",
      value: formatToolOptionValue(input.query ?? result.query),
    });
    details.push({
      label: "Platform",
      value: formatToolOptionValue(input.platform_level),
    });
    details.push({
      label: "Quality",
      value: formatToolOptionValue(input.preferred_quality),
    });
    details.push({
      label: "Sort",
      value: formatToolOptionValue(input.sort),
    });
  } else if (part.type === "tool-get_similar_worlds") {
    details.push({
      label: "Source World",
      value: result.sourceWorld?.name ?? formatToolOptionValue(input.world_id),
    });
    details.push({
      label: "World ID",
      value: formatToolOptionValue(input.world_id ?? result.sourceWorld?.id),
    });
    details.push({
      label: "Platform",
      value: formatToolOptionValue(input.platform_level),
    });
    details.push({
      label: "Quality",
      value: formatToolOptionValue(input.preferred_quality),
    });
    details.push({
      label: "Sort",
      value: formatToolOptionValue(input.sort),
    });
  } else {
    for (const [key, value] of Object.entries(input)) {
      details.push({
        label: key,
        value: formatToolOptionValue(value),
      });
    }
  }

  return details;
}

function ToolCallDetails({
  part,
  result,
}: {
  part: MessagePartLike;
  result: SearchToolResult;
}) {
  const details = useMemo(() => buildToolDetails(part, result), [part, result]);

  return (
    <div className="tool__details" aria-label="Tool call details">
      {details.map((detail) => (
        <div className="tool__detail-row" key={detail.label}>
          <span className="tool__detail-label">{detail.label}</span>
          <span className="tool__detail-value">{detail.value}</span>
        </div>
      ))}
    </div>
  );
}

function ToolAccordionSummary({
  label,
}: {
  label: string;
}) {
  return (
    <summary className="tool__summary" title="ツール結果の詳細を開閉">
      <span className="tool__summary-label">
        <span className="tool__summary-caret" aria-hidden="true">
          &gt;
        </span>
        {label}
      </span>
    </summary>
  );
}

function ChargeSummary({ charge }: { charge: ChargeMetadata }) {
  const hasExtendedCharge = charge.additionalPoints > 0;

  return (
    <p className="message__charge">
      <span
        className="message__charge-label"
        title="この返答で消費したエナジー"
      >
        エナジー消費:
      </span>{" "}
      <span className="message__charge-value" title="基本消費量">
        {formatPoints(charge.basePoints)}pt
      </span>
      {hasExtendedCharge ? (
        <>
          {" "}
          <span className="message__charge-plus">+</span>{" "}
          <span
            className="message__charge-value"
            title="追加で発生した拡張消費量"
          >
            {formatPoints(charge.additionalPoints)}pt
          </span>{" "}
          <span className="message__charge-meta" title="追加処理ぶんの消費">
            (拡張消費)
          </span>
        </>
      ) : null}
    </p>
  );
}

export function buildSessionWorldRegistry(messages: RenderableMessage[]) {
  const registry: SessionWorldRegistry = {};

  for (const message of messages) {
    for (const part of message.parts ?? []) {
      if (
        part.type.startsWith("tool-") &&
        part.state === "output-available" &&
        part.output
      ) {
          const result = part.output as SearchToolResult;
        for (const world of result.worlds ?? []) {
          const ref = getWorldRefNumber(world.ref);
          if (ref > 0) {
            registry[ref] = world;
          }
        }
      }
    }
  }

  return registry;
}

function renderPart(
  part: MessagePartLike,
  key: string,
  registry: SessionWorldRegistry
) {
  if (part.type === "reasoning") {
    const reasoningText = part.text?.trim();

    if (!reasoningText) {
      return null;
    }

    return <ReasoningAccordion key={key} text={reasoningText} state={part.state} />;
  }

  if (part.type === "text") {
    return <MarkdownText key={key} text={part.text ?? ""} registry={registry} />;
  }

  if (part.type.startsWith("tool-")) {
    if (part.state === "output-error") {
      return (
        <div className="tool tool--error" key={key}>
          {part.errorText ?? "ツール実行中にエラーが発生しました。"}
        </div>
      );
    }

    if (part.state === "output-available" && part.output) {
      const result = part.output as SearchToolResult;

      return (
        <details className="tool tool--accordion" key={key}>
          <ToolAccordionSummary label="ツールを呼び出しました" />

          <div className="tool__content">
            <div className="tool__content-inner">
              <div className="tool__card">
                <div className="tool__header">
                  <div>
                    <div className="tool__eyebrow">Tool Result</div>
                    <h3>{result.title}</h3>
                  </div>
                  <p className="tool__meta">
                    {result.worlds.length}件
                    {result.worlds.length > 0
                      ? ` | ref:${formatWorldRefLabel(result.worlds[0].ref)}-${
                          formatWorldRefLabel(result.worlds[result.worlds.length - 1].ref)
                        }`
                      : ""}
                  </p>
                </div>

                <ToolCallDetails part={part} result={result} />
                <WorldCarousel ariaLabel={result.title} worlds={result.worlds} />
              </div>
            </div>
          </div>
        </details>
      );
    }

    return (
      <details className="tool tool--accordion tool--accordion-pending" key={key}>
        <ToolAccordionSummary label="ツールを呼び出しました" />
        <div className="tool__content">
          <div className="tool__content-inner">
            <div className="tool tool--pending">
              検索ツールを実行しています。
            </div>
          </div>
        </div>
      </details>
    );
  }

  return null;
}

export const MessageBubble = memo(function MessageBubble({
  message,
  registry,
}: {
  message: RenderableMessage;
  registry: SessionWorldRegistry;
}) {
  return (
    <div className={`message message--${message.role}`}>
      <div className="message__role">
        {message.role === "user" ? "You" : "AstroLens"}
      </div>

      <div className="message__body">
        {(message.parts ?? []).map((part, index) => (
          <Fragment key={`${message.id}-${index}`}>
            {renderPart(part, `${message.id}-${index}`, registry)}
          </Fragment>
        ))}

        {message.role === "assistant" && message.metadata?.charge ? (
          <ChargeSummary charge={message.metadata.charge} />
        ) : null}
      </div>
    </div>
  );
});
